"use client";

import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getTouchControlsLayout,
  useTouchControlsSettings,
  type TouchControlName,
} from "../hooks/useTouchControlsSettings";

export type LegacyAction = "left" | "right" | "softDrop" | "hardDrop" | "cw" | "ccw";

type TouchControlsProps = {
  disabled?: boolean;
  onInput: (action: LegacyAction, held: boolean) => void;
  side: "left" | "right";
  variant?: "default" | "practice";
};

type ControlButton = {
  action: LegacyAction;
  glyph: string;
  label: string;
};

type ButtonBehavior = "tap" | "press";
type ButtonAction = LegacyAction | readonly LegacyAction[];

type JoystickPoint = Readonly<{
  x: number;
  y: number;
}>;

type VirtualJoystickProps = {
  disabled: boolean;
  onInput: (action: LegacyAction, held: boolean) => void;
};

function VirtualJoystick({ disabled, onInput }: VirtualJoystickProps) {
  const padRef = useRef<HTMLDivElement | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const activeActionsRef = useRef<readonly LegacyAction[]>([]);
  const [knob, setKnob] = useState<JoystickPoint>({ x: 0, y: 0 });
  const [active, setActive] = useState(false);

  const setActions = useCallback((nextActions: readonly LegacyAction[]) => {
    const currentActions = activeActionsRef.current;
    for (const action of currentActions) {
      if (!nextActions.includes(action)) onInput(action, false);
    }
    for (const action of nextActions) {
      if (!currentActions.includes(action)) onInput(action, true);
    }
    activeActionsRef.current = nextActions;
  }, [onInput]);

  const finish = useCallback(() => {
    setActions([]);
    pointerIdRef.current = null;
    setKnob({ x: 0, y: 0 });
    setActive(false);
  }, [setActions]);

  const updateFromPoint = useCallback((clientX: number, clientY: number) => {
    const pad = padRef.current;
    if (!pad) return;
    const rect = pad.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const dx = clientX - centerX;
    const dy = clientY - centerY;
    const distance = Math.hypot(dx, dy);
    const maxTravel = Math.max(20, rect.width * 0.28);
    const scale = distance > maxTravel ? maxTravel / distance : 1;
    setKnob({ x: dx * scale, y: dy * scale });

    const deadZone = Math.max(16, rect.width * 0.16);
    const nextActions: LegacyAction[] = [];
    if (Math.abs(dx) >= deadZone) nextActions.push(dx < 0 ? "left" : "right");
    if (dy >= deadZone) nextActions.push("softDrop");
    setActions(nextActions);
  }, [setActions]);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || pointerIdRef.current !== null) return;
    event.preventDefault();
    event.stopPropagation();
    pointerIdRef.current = event.pointerId;
    event.currentTarget.setPointerCapture(event.pointerId);
    setActive(true);
    navigator.vibrate?.(4);
    updateFromPoint(event.clientX, event.clientY);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerId !== pointerIdRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    updateFromPoint(event.clientX, event.clientY);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerId !== pointerIdRef.current) return;
    event.stopPropagation();
    finish();
  };

  useEffect(() => {
    const releasePointer = (event: PointerEvent) => {
      if (event.pointerId === pointerIdRef.current) finish();
    };
    window.addEventListener("pointerup", releasePointer);
    window.addEventListener("pointercancel", releasePointer);
    window.addEventListener("blur", finish);
    document.addEventListener("visibilitychange", finish);
    return () => {
      window.removeEventListener("pointerup", releasePointer);
      window.removeEventListener("pointercancel", releasePointer);
      window.removeEventListener("blur", finish);
      document.removeEventListener("visibilitychange", finish);
      finish();
    };
  }, [finish]);

  useEffect(() => {
    if (disabled) finish();
  }, [disabled, finish]);

  return (
    <div
      aria-label="仮想ジョイスティック：左右移動、下でソフトドロップ"
      className="virtual-joystick-shell"
    >
      <div
        className={`virtual-joystick${active ? " is-active" : ""}`}
        onPointerCancel={handlePointerUp}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        ref={padRef}
        role="application"
        tabIndex={-1}
      >
        <span aria-hidden="true" className="virtual-joystick-direction virtual-joystick-left">←</span>
        <span aria-hidden="true" className="virtual-joystick-direction virtual-joystick-right">→</span>
        <span aria-hidden="true" className="virtual-joystick-direction virtual-joystick-down">▼</span>
        <span
          aria-hidden="true"
          className="virtual-joystick-knob"
          style={{ left: `calc(50% + ${knob.x}px)`, top: `calc(50% + ${knob.y}px)` }}
        />
      </div>
    </div>
  );
}

const buttons: ControlButton[] = [
  { action: "ccw", glyph: "↺", label: "左回転" },
  { action: "left", glyph: "◀", label: "左移動" },
  { action: "softDrop", glyph: "▼", label: "ソフト落下" },
  { action: "right", glyph: "▶", label: "右移動" },
  { action: "cw", glyph: "↻", label: "右回転" },
];

export function TouchControls({ disabled = false, onInput, side, variant = "default" }: TouchControlsProps) {
  const { settings } = useTouchControlsSettings();
  const pressed = useRef(new Map<number, readonly LegacyAction[]>());

  const actionsFor = (action: ButtonAction): readonly LegacyAction[] => (
    typeof action === "string" ? [action] : action
  );

  const release = useCallback(
    (pointerId: number) => {
      const actions = pressed.current.get(pointerId);
      if (!actions) return;
      pressed.current.delete(pointerId);
      for (const action of actions) onInput(action, false);
    },
    [onInput],
  );

  const releaseAll = useCallback(() => {
    for (const actions of pressed.current.values()) {
      for (const action of actions) onInput(action, false);
    }
    pressed.current.clear();
  }, [onInput]);

  const releasePointer = useCallback((event: PointerEvent) => {
    release(event.pointerId);
  }, [release]);

  const pulse = useCallback((action: LegacyAction) => {
    onInput(action, true);
    // Span two input samples so the engine's alternating repeat counter
    // always produces exactly one horizontal move per tap.
    window.setTimeout(() => onInput(action, false), 95);
  }, [onInput]);

  const button = (
    action: ButtonAction,
    glyph: string,
    label: string,
    className = "",
    behavior: ButtonBehavior = "press",
    layoutName?: TouchControlName,
  ) => {
    const layout = getTouchControlsLayout(settings);
    const actions = actionsFor(action);
    const position = layout[layoutName ?? actions[0]];
    const actionClass = actions.length > 1 ? "softDropDiagonal" : actions[0];
    const style: CSSProperties | undefined = variant === "practice" && position
      ? { left: `${position.x}%`, top: `${position.y}%` }
      : undefined;

    return (
    <button
      aria-label={label}
      className={`control-button control-${actionClass}${variant === "practice" ? " touch-positioned" : ""} ${className}`}
      disabled={disabled}
      onContextMenu={(event) => event.preventDefault()}
      onPointerCancel={(event) => {
        event.stopPropagation();
        release(event.pointerId);
      }}
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
        navigator.vibrate?.(6);
        if (behavior === "tap") {
          for (const nextAction of actions) pulse(nextAction);
          return;
        }
        event.currentTarget.setPointerCapture(event.pointerId);
        pressed.current.set(event.pointerId, actions);
        for (const nextAction of actions) onInput(nextAction, true);
      }}
      onPointerUp={(event) => {
        event.stopPropagation();
        release(event.pointerId);
      }}
      style={style}
      type="button"
    >
      <span aria-hidden="true">{glyph}</span>
      <small>{label}</small>
    </button>
    );
  };

  const rotationControls = (
    <div className="rotation-controls" aria-label="回転操作">
      {button("ccw", "↺", "左回転", "rotation-button", "press")}
      {button("cw", "↻", "右回転", "rotation-button", "press")}
    </div>
  );

  useEffect(() => {
    if (settings.visible && (variant !== "practice" || settings.inputMode === "buttons")) return;
    releaseAll();
  }, [releaseAll, settings.inputMode, settings.visible, variant]);

  useEffect(() => {
    window.addEventListener("blur", releaseAll);
    // A pointerup/pointercancel belongs to one finger. Releasing every
    // pointer here would make a second simultaneously held button stop when
    // the first finger is lifted.
    window.addEventListener("pointerup", releasePointer);
    window.addEventListener("pointercancel", releasePointer);
    document.addEventListener("visibilitychange", releaseAll);
    return () => {
      window.removeEventListener("blur", releaseAll);
      window.removeEventListener("pointerup", releasePointer);
      window.removeEventListener("pointercancel", releasePointer);
      document.removeEventListener("visibilitychange", releaseAll);
    };
  }, [releaseAll, releasePointer]);

  if (!settings.visible) return null;

  if (variant === "practice") {
    if (settings.inputMode === "joystick") {
      return (
        <div
          aria-label="ジョイスティック操作"
          className="touch-controls-layer touch-joystick-layer"
          style={{ opacity: 1 - settings.transparency }}
        >
          <VirtualJoystick disabled={disabled} onInput={onInput} />
          {rotationControls}
        </div>
      );
    }
    if (settings.inputMode !== "buttons") return null;
    return (
      <div
        aria-label="ジョイスティック操作"
        className="touch-controls-layer touch-joystick-layer"
        style={{ opacity: 1 - settings.transparency }}
      >
        <div className="touch-action-bar" aria-label="落下操作">
          {button("left", "←", "左へ1マス", "touch-action-button", "tap", "left")}
          {button("softDrop", "▼", "ソフトドロップ（長押し）", "touch-action-button", "press", "softDrop")}
          {button("right", "→", "右へ1マス", "touch-action-button", "tap", "right")}
        </div>
        {rotationControls}
      </div>
    );
  }

  return (
    <div
      aria-label="ゲーム操作"
      className={`touch-controls controls-${side}`}
      style={{ opacity: 1 - settings.transparency }}
    >
      {buttons.map(({ action, glyph, label }) => button(action, glyph, label))}
    </div>
  );
}
